if (!customElements.get('kit-builder')) {
  customElements.define(
    'kit-builder',
    class KitBuilder extends HTMLElement {
      connectedCallback() {
        this.rate = (Number(this.dataset.gstRate) || 0) / 100;
        this.taxesIncluded = this.dataset.taxesIncluded === 'true';
        this.storageKey = `kit-builder:${this.dataset.sectionId}`;
        this.items = Array.from(this.querySelectorAll('[data-kit-item]'));

        this.el = {
          count: this.querySelector('[data-kit-count]'),
          empty: this.querySelector('[data-kit-empty]'),
          lines: this.querySelector('[data-kit-lines]'),
          ex: this.querySelector('[data-kit-total-ex]'),
          gst: this.querySelector('[data-kit-total-gst]'),
          inc: this.querySelector('[data-kit-total-inc]'),
          error: this.querySelector('[data-kit-error]'),
          checkout: this.querySelector('[data-kit-checkout]'),
          add: this.querySelector('[data-kit-add]'),
          clear: this.querySelector('[data-kit-clear]'),
          summary: this.querySelector('[data-kit-summary]'),
          bar: this.querySelector('[data-kit-bar]'),
          barCount: this.querySelector('[data-kit-bar-count]'),
          barTotal: this.querySelector('[data-kit-bar-total]'),
        };

        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('change', this.onChange.bind(this));
        this.el.checkout.addEventListener('click', () => this.submit('checkout'));
        this.el.add.addEventListener('click', () => this.submit('cart'));
        this.el.clear.addEventListener('click', () => this.clear());
        this.querySelector('[data-kit-review]').addEventListener('click', () =>
          this.el.summary.scrollIntoView({ behavior: 'smooth', block: 'start' })
        );

        this.restore();
        this.items.forEach((item) => this.syncItem(item));
        this.render();
        this.watchSummary();
      }

      onClick(event) {
        const button = event.target.closest('[data-qty-change]');
        if (button) {
          const input = button.closest('[data-kit-item]').querySelector('[data-kit-qty]');
          this.setQty(input, Number(input.value) + Number(button.dataset.qtyChange));
          return;
        }
        const remove = event.target.closest('[data-kit-remove]');
        if (remove) {
          const item = this.items.find((i) => i.dataset.productId === remove.dataset.kitRemove);
          if (item) this.setQty(item.querySelector('[data-kit-qty]'), 0);
        }
      }

      onChange(event) {
        const item = event.target.closest('[data-kit-item]');
        if (!item) return;
        if (event.target.matches('[data-kit-qty]')) this.setQty(event.target, event.target.value);
        if (event.target.matches('[data-kit-variant]')) {
          this.syncItem(item);
          this.render();
        }
      }

      setQty(input, value) {
        const qty = Math.max(0, Math.min(99, Math.floor(Number(value)) || 0));
        input.value = qty;
        this.render();
      }

      // Copies the chosen option's id and price onto the card and refreshes its ex/inc GST price labels.
      syncItem(item) {
        const select = item.querySelector('[data-kit-variant]');
        const option = select ? select.selectedOptions[0] : null;
        if (option) {
          item.dataset.variantId = option.value;
          item.dataset.price = option.dataset.price;
          item.dataset.variantTitle = option.textContent.trim();
        }
        const { ex, inc } = this.split(Number(item.dataset.price));
        const exLabel = item.querySelector('[data-item-price-ex]');
        const incLabel = item.querySelector('[data-item-price-inc]');
        if (exLabel) exLabel.textContent = this.formatMoney(ex);
        if (incLabel) incLabel.textContent = this.formatMoney(inc);
      }

      // Store prices may be entered with or without GST; returns both versions in cents.
      split(cents) {
        if (this.taxesIncluded) return { ex: Math.round(cents / (1 + this.rate)), inc: cents };
        return { ex: cents, inc: Math.round(cents * (1 + this.rate)) };
      }

      selection() {
        return this.items
          .map((item) => ({ item, qty: Number(item.querySelector('[data-kit-qty]').value) || 0 }))
          .filter(({ qty }) => qty > 0);
      }

      render() {
        const selected = this.selection();
        let units = 0;
        let lineTotal = 0;

        this.items.forEach((item) => {
          const qty = Number(item.querySelector('[data-kit-qty]').value) || 0;
          item.classList.toggle('is-selected', qty > 0);
        });

        this.el.lines.replaceChildren(
          ...selected.map(({ item, qty }) => {
            const price = Number(item.dataset.price);
            units += qty;
            lineTotal += price * qty;

            const li = document.createElement('li');
            li.className = 'kit-builder__line';
            const name = document.createElement('span');
            name.className = 'kit-builder__line-name';
            name.textContent = `${qty} × ${item.dataset.productTitle}`;
            if (item.dataset.variantTitle) {
              const variant = document.createElement('small');
              variant.textContent = item.dataset.variantTitle;
              name.append(variant);
            }
            const amount = document.createElement('span');
            amount.className = 'kit-builder__line-price';
            amount.textContent = this.formatMoney(this.split(price * qty).ex);
            const remove = document.createElement('button');
            remove.type = 'button';
            remove.className = 'kit-builder__line-remove';
            remove.dataset.kitRemove = item.dataset.productId;
            remove.setAttribute('aria-label', `Remove ${item.dataset.productTitle}`);
            remove.textContent = '×';
            li.append(name, amount, remove);
            return li;
          })
        );

        // GST is worked out on the order subtotal, as it is at checkout.
        let ex;
        let inc;
        if (this.taxesIncluded) {
          inc = lineTotal;
          ex = Math.round(lineTotal / (1 + this.rate));
        } else {
          ex = lineTotal;
          inc = Math.round(lineTotal * (1 + this.rate));
        }

        this.el.ex.textContent = this.formatMoney(ex);
        this.el.gst.textContent = this.formatMoney(inc - ex);
        this.el.inc.textContent = this.formatMoney(inc);

        const hasItems = units > 0;
        this.el.empty.hidden = hasItems;
        this.el.count.hidden = !hasItems;
        this.el.count.textContent = `${units} ${units === 1 ? 'item' : 'items'}`;
        this.el.clear.hidden = !hasItems;
        this.el.checkout.disabled = !hasItems;
        this.el.add.disabled = !hasItems;
        this.el.barCount.textContent = `${units} ${units === 1 ? 'item' : 'items'}`;
        this.el.barTotal.textContent = `${this.formatMoney(inc)} inc GST`;
        this.hasItems = hasItems;
        this.updateBar();
        this.save();
      }

      clear() {
        this.items.forEach((item) => (item.querySelector('[data-kit-qty]').value = 0));
        this.el.error.hidden = true;
        this.render();
      }

      submit(destination) {
        const items = this.selection().map(({ item, qty }) => ({ id: Number(item.dataset.variantId), quantity: qty }));
        if (!items.length) return;

        const button = destination === 'checkout' ? this.el.checkout : this.el.add;
        this.el.error.hidden = true;
        button.classList.add('loading');
        button.setAttribute('aria-disabled', true);
        button.querySelector('.loading__spinner')?.classList.remove('hidden');

        fetch(`${this.dataset.cartAddUrl}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) throw new Error(response.description || response.message || 'Something went wrong.');
            this.clear();
            window.location = destination === 'checkout' ? `${window.Shopify.routes.root}checkout` : this.dataset.cartUrl;
          })
          .catch((error) => {
            this.el.error.textContent = error.message;
            this.el.error.hidden = false;
            button.classList.remove('loading');
            button.removeAttribute('aria-disabled');
            button.querySelector('.loading__spinner')?.classList.add('hidden');
          });
      }

      // On small screens a bar with the running total stays on screen until the summary panel is visible.
      watchSummary() {
        if (!('IntersectionObserver' in window)) return;
        new IntersectionObserver(
          (entries) => {
            this.summaryVisible = entries[0].isIntersecting;
            this.updateBar();
          },
          { threshold: 0.15 }
        ).observe(this.el.summary);
        new IntersectionObserver((entries) => {
          this.sectionVisible = entries[0].isIntersecting;
          this.updateBar();
        }).observe(this);
      }

      updateBar() {
        this.el.bar.hidden = !(this.hasItems && this.sectionVisible && !this.summaryVisible);
      }

      // Remembers the selection for this browser tab, so it survives visiting a product page and coming back.
      save() {
        try {
          const state = this.selection().map(({ item, qty }) => [item.dataset.productId, item.dataset.variantId, qty]);
          sessionStorage.setItem(this.storageKey, JSON.stringify(state));
        } catch (e) {}
      }

      restore() {
        let state = [];
        try {
          state = JSON.parse(sessionStorage.getItem(this.storageKey) || '[]');
        } catch (e) {}
        state.forEach(([productId, variantId, qty]) => {
          const item = this.items.find((i) => i.dataset.productId === String(productId));
          if (!item || item.classList.contains('kit-item--sold-out')) return;
          const select = item.querySelector('[data-kit-variant]');
          const option = select && Array.from(select.options).find((o) => o.value === String(variantId) && !o.disabled);
          if (option) select.value = option.value;
          item.querySelector('[data-kit-qty]').value = qty;
        });
      }

      formatMoney(cents) {
        const format = this.dataset.moneyFormat || '${{amount}}';
        return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
          const commaDecimal = key.includes('comma_separator');
          const [whole, decimals] = (cents / 100).toFixed(key.includes('no_decimals') ? 0 : 2).split('.');
          const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, commaDecimal ? '.' : ',');
          return decimals ? `${grouped}${commaDecimal ? ',' : '.'}${decimals}` : grouped;
        });
      }
    }
  );
}
